import {
    AffinityFilterChips,
    GoogleRisingClusterFilterChips,
    InstagramAffinityFilterChips,
    PopularityIndexAffinityFilterChips,
    PricePointFilterChips,
    TikTokAffinityFilterChips,
} from './FilterChips';
import FilterSelect from './Input/FilterSelect';
import { healthStatFilterOptions } from 'utils/filters';
import styled from 'styled-components';
import { DATA_SOURCES } from '@/config/constants';
import { PARENT_NAME_FILTER_KEYS, SOURCES } from 'utils/dataSources';

const pricePointFilter = healthStatFilterOptions.find(
    ({ label }) => label === 'price point',
);

const Wrapper = styled.div`
    display: flex;
    align-items: flex-start;
`;

export const getOnChange =
    (filterKey, filters, setFilter) =>
    ({ action, option }) => {
        switch (action) {
            case 'select-option':
                setFilter(filterKey, [...(filters[filterKey] || []), option]);
                break;
            case 'remove-value':
                setFilter(
                    filterKey,
                    filters[filterKey].filter(
                        ({ value }) => value !== option.value,
                    ),
                );
                break;
            default:
                throw new Error(`Unexpected action type "${action}"`);
        }
    };

// Keyed on the descriptor so a new source needs an entry here and nothing else.
// The DOM ids are a contract with QA selectors and Mixpanel funnels rather than
// with any code in this repo, so they are carried verbatim.
const AFFINITY_CHIPS = {
    [DATA_SOURCES.GOOGLE]: {
        Chips: AffinityFilterChips,
        id: 'data-table-affinity-group-filter',
    },
    [DATA_SOURCES.TIKTOK]: {
        Chips: TikTokAffinityFilterChips,
        id: 'data-table-tiktok-affinity-group-filter',
    },
    [DATA_SOURCES.INSTAGRAM]: {
        Chips: InstagramAffinityFilterChips,
        id: 'data-table-instagram-affinity-group-filter',
    },
    [DATA_SOURCES.POPULARITY]: {
        Chips: PopularityIndexAffinityFilterChips,
        id: 'data-table-pop-index-affinity-group-filter',
    },
};

const SUPPORTED_FILTERS = [
    ...PARENT_NAME_FILTER_KEYS,
    'cluster',
    'healthStats',
];

function SectionTableFilterChips({
    handleFilter,
    filters,
    setFilter,
    setFilters,
    queryData,
    showGroupChips = true,
    showAllGroupOptions = false,
}) {
    const { healthStats, ...filtersSansHealthStats } = filters;
    const filterNames = SUPPORTED_FILTERS.filter((name) => handleFilter[name]);
    const resetMenuFilters = () => {
        const cluster =
            filterNames.includes('parentName') ||
            filterNames.includes('tikTokParentName') ||
            filterNames.includes('popIndexParentName') ||
            filterNames.includes('healthStats')
                ? []
                : (filters.cluster || []).filter(({ value }) =>
                      value.includes('ris'),
                  );
        const nextHealthStats = healthStats || [];
        const nextFilters = {};

        if (nextHealthStats.length) {
            nextFilters.healthStats = nextHealthStats;
        }

        if (cluster.length) {
            nextFilters.cluster = cluster;
        }

        setFilters(nextFilters);
    };
    const mixpanelMeta = { contentKey: 'DataTable' };
    const menus = filterNames.filter((name) => name !== 'healthStats');
    const filterSelect =
        menus && menus.length ? (
            <span id="data-table-filter-select">
                <FilterSelect
                    filters={filtersSansHealthStats}
                    setFilter={setFilter}
                    resetFilters={resetMenuFilters}
                    mixpanelMeta={mixpanelMeta}
                    menus={menus}
                    getOnChange={getOnChange}
                    menuAlign="left"
                    queryData={queryData}
                    showAllGroupOptions={showAllGroupOptions}
                />
            </span>
        ) : null;
    let nonRisingClustersOnly = false;
    let chips = null;
    // One shape per source, differing only in the component and the DOM id, so
    // the branch is a registry lookup. `parentSource` can legitimately be
    // undefined — SectionTable's default handleFilter is { cluster, name }, with
    // no parent-name key at all — and the if/else chain this replaces tolerated
    // that by falling through. Losing that guard is a TypeError on every plain
    // section table.
    const parentSource = Object.values(SOURCES).find(
        ({ parentNameFilterKey }) =>
            parentNameFilterKey && filterNames.includes(parentNameFilterKey),
    );
    const affinity = parentSource && AFFINITY_CHIPS[parentSource.key];
    if (affinity) {
        const filterKey = parentSource.parentNameFilterKey;
        chips = showGroupChips ? (
            <affinity.Chips
                appliedFilters={
                    filters[filterKey]
                        ? filters[filterKey].map(({ value }) => value)
                        : null
                }
                onChange={getOnChange(filterKey, filters, setFilter)}
                mixpanelMeta={mixpanelMeta}
                menus={menus}
                filterNames={filterNames}
                filtersSansHealthStats={filtersSansHealthStats}
                filters={filters}
                setFilter={setFilter}
                setFilters={setFilters}
                getOnChange={getOnChange}
                queryData={queryData}
                id={affinity.id}
            />
        ) : (
            filterSelect
        );
    } else if (filterNames.includes('healthStats')) {
        chips = (
            <>
                <PricePointFilterChips
                    filters={filters}
                    setFilter={setFilter}
                    onChange={getOnChange(
                        pricePointFilter.filter,
                        filters,
                        setFilter,
                    )}
                    mixpanelMeta={mixpanelMeta}
                    id="data-table-price-point-filter"
                >
                    {filterSelect}
                </PricePointFilterChips>
            </>
        );
    } else if (filterNames.includes('cluster')) {
        nonRisingClustersOnly = true;
        chips = (
            <>
                <GoogleRisingClusterFilterChips
                    filters={filters}
                    onChange={getOnChange('cluster', filters, setFilter)}
                    mixpanelMeta={mixpanelMeta}
                    id="data-table-cluster-filter"
                >
                    <span id="data-table-filter-select">
                        <FilterSelect
                            filters={filtersSansHealthStats}
                            setFilter={setFilter}
                            resetFilters={resetMenuFilters}
                            mixpanelMeta={mixpanelMeta}
                            menus={menus}
                            getOnChange={getOnChange}
                            menuAlign="left"
                            nonRisingClustersOnly={nonRisingClustersOnly}
                        />
                    </span>
                </GoogleRisingClusterFilterChips>
            </>
        );
    }
    return <Wrapper id="data-table-filters">{chips}</Wrapper>;
}

export default SectionTableFilterChips;
